import React, { useState } from "react"
import styled from "styled-components"

const Wrapper = styled.div`
  position: relative;
  overflow: hidden;

  margin: ${props => props.theme.sizes.mobile} 0;

  background: rgba(224, 226, 228, 0.055);
  border-left: 4px solid
    ${props =>
      props.$open ? props.theme.colors.orange : "rgba(224, 226, 228, 0.24)"};

  transition: background 0.25s cubic-bezier(0.16, 1.08, 0.38, 0.98),
    border-color 0.25s cubic-bezier(0.16, 1.08, 0.38, 0.98);

  &:hover {
    background: rgba(224, 226, 228, 0.08);
  }

  ${props =>
    props.$open &&
    `
      background: rgba(236, 165, 58, 0.105);

      &:hover {
        background: rgba(236, 165, 58, 0.13);
      }

      &:after {
        content: "";
        position: absolute;
        top: 0;
        right: 0;
        width: 30%;
        height: 100%;
        background: linear-gradient(
          90deg,
          rgba(236, 165, 58, 0),
          rgba(236, 165, 58, 0.08)
        );
        pointer-events: none;
      }
    `}

  ${props => props.theme.above.desktop`
    margin: ${props.theme.sizes.desktop} 0;
  `}
`

const Toggle = styled.button`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;

  width: 100%;

  padding: calc(${props => props.theme.sizes.mobile} / 1.35)
    calc(${props => props.theme.sizes.mobile} / 1.25)
    calc(${props => props.theme.sizes.mobile} / 2);

  color: currentColor;
  background: transparent;
  border: 0;

  text-align: left;
  cursor: pointer;

  &:focus {
    outline: 2px solid ${props => props.theme.colors.orange};
    outline-offset: -2px;
  }

  &:focus:not(:focus-visible) {
    outline: 0;
  }

  ${props => props.theme.above.desktop`
    padding: calc(${props.theme.sizes.desktop} / 1.35)
      calc(${props.theme.sizes.desktop} / 1.25)
      calc(${props.theme.sizes.desktop} / 2);
  `}
`

const Label = styled.span`
  display: block;

  font-family: ${props => props.theme.fontFamilies.plainRegular};
  font-size: ${props => props.theme.fontSizes.mobile.m};
  line-height: 1.25;

  ${props => props.theme.above.desktop`
    font-size: ${props.theme.fontSizes.desktop.p};
  `}
`

const Teaser = styled.div`
  display: block;

  margin-bottom: calc(${props => props.theme.sizes.mobile} / 1.35);
  padding: 0 calc(${props => props.theme.sizes.mobile} / 1.25);

  font-family: ${props => props.theme.fontFamilies.plainLight};
  font-size: ${props => props.theme.fontSizes.mobile.s};
  line-height: 1.4;

  opacity: 0.72;

  ${props => props.theme.above.desktop`
    margin-bottom: calc(${props.theme.sizes.desktop} / 1.35);
    padding: 0 calc(${props.theme.sizes.desktop} / 1.25);

    font-size: ${props.theme.fontSizes.desktop.m};
  `}
`

const Icon = styled.span`
  position: relative;
  flex: 0 0 auto;

  width: 18px;
  height: 18px;

  margin-top: 2px;
  margin-left: ${props => props.theme.sizes.mobile};

  &:before,
  &:after {
    content: "";
    position: absolute;

    top: 50%;
    left: 50%;

    width: 100%;
    height: 2px;

    background-color: ${props => props.theme.colors.orange};

    transform: translate(-50%, -50%);
    transition: transform 0.25s cubic-bezier(0.16, 1.08, 0.38, 0.98);
  }

  &:after {
    transform: translate(-50%, -50%) rotate(90deg);
  }

  ${props =>
    props.$open &&
    `
      &:after {
        transform: translate(-50%, -50%) rotate(0deg);
      }
    `}

  ${props => props.theme.above.desktop`
    width: ${props.theme.desktopVW(22)};
    height: ${props.theme.desktopVW(22)};

    margin-left: ${props.theme.sizes.desktop};
  `}
`

const Content = styled.div`
  display: ${props => (props.$open ? "block" : "none")};

  margin: 0 calc(${props => props.theme.sizes.mobile} / 1.25);
  padding: calc(${props => props.theme.sizes.mobile} / 1.35) 0
    calc(${props => props.theme.sizes.mobile} / 1.1);

  border-top: 1px solid rgba(224, 226, 228, 0.17);

  ${props => props.theme.above.desktop`
    margin: 0 calc(${props.theme.sizes.desktop} / 1.25);
    padding: calc(${props.theme.sizes.desktop} / 1.35) 0
      calc(${props.theme.sizes.desktop} / 1.1);
  `}
`

const AccordionItem = ({
  className,
  id,
  label,
  teaser,
  content,
  defaultOpen,
}) => {
  const [open, setOpen] = useState(defaultOpen || false)
  const displayLabel = label || "Read more"
  const panelId = id ? `${id}-content` : undefined

  if (!content) return null

  return (
    <Wrapper className={className} $open={open}>
      <Toggle
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen(!open)}
      >
        <Label>{displayLabel}</Label>
        <Icon $open={open} aria-hidden="true" />
      </Toggle>
      {teaser && <Teaser>{teaser}</Teaser>}
      <Content id={panelId} $open={open}>
        {content}
      </Content>
    </Wrapper>
  )
}

export default AccordionItem
